import PropTypes from "prop-types";
import { useState, useEffect } from "react";

import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";

function getTranslatedRole(role, t) {
  const normalizedRole = String(role || "")
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/_/g, "");

  if (normalizedRole === "superadmin" || normalizedRole === "superadministrator") {
    return t ? t("navigation.superAdmin", "Super Admin") : "Super Admin";
  }
  if (normalizedRole === "admin") {
    return t ? t("navigation.admin", "Admin") : "Admin";
  }
  if (normalizedRole === "annotator") {
    return t ? t("navigation.annotator", "Annotator") : "Annotator";
  }

  return role || "-";
}

function ActionButtons({ row, onEdit, onReset, onDelete, t }) {
  const user = row.original;
  return (
    <>
      <MDButton
        variant="text"
        color="info"
        onClick={() => onEdit(user)}
        title={t ? t("superAdmin.table.actions.edit", "Edit") : "Edit"}
      >
        <Icon>edit</Icon>
      </MDButton>
      <MDButton
        variant="text"
        color="warning"
        onClick={() => onReset(user)}
        title={t ? t("superAdmin.table.actions.resetPassword", "Reset Password") : "Reset Password"}
      >
        <Icon>password</Icon>
      </MDButton>
      <MDButton
        variant="text"
        color="error"
        onClick={() => onDelete(user)}
        title={t ? t("superAdmin.table.actions.delete", "Delete") : "Delete"}
      >
        <Icon>delete</Icon>
      </MDButton>
    </>
  );
}

ActionButtons.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  t: PropTypes.func,
};

ActionButtons.defaultProps = {
  t: null,
};

export default function UsersDataTable({ users, onEdit, onReset, onDelete, t }) {
  // Define columns with translation support
  const columns = [
    {
      Header: t ? t("superAdmin.table.columns.actions", "Actions") : "Actions",
      accessor: "actions",
      align: "center",
      Cell: (props) => (
        <ActionButtons {...props} onEdit={onEdit} onReset={onReset} onDelete={onDelete} t={t} />
      ),
      width: "150px",
    },
    {
      Header: t ? t("superAdmin.table.columns.name", "Name") : "Name",
      accessor: "first_name",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: t ? t("superAdmin.table.columns.role", "Role") : "Role",
      accessor: "role",
      Cell: ({ value }) => getTranslatedRole(value, t),
    },
    {
      Header: t ? t("superAdmin.table.columns.phoneNumber", "Phone Number") : "Phone Number",
      accessor: "phone_number",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: t ? t("superAdmin.table.columns.email", "Email") : "Email",
      accessor: "email_id",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: t ? t("superAdmin.table.columns.state", "State") : "State",
      accessor: "state",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: t ? t("superAdmin.table.columns.district", "District") : "District",
      accessor: "district",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: t ? t("superAdmin.table.columns.programName", "Program Name") : "Program Name",
      accessor: "program_name",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: t ? t("superAdmin.table.columns.samples", "Samples") : "Samples",
      accessor: "assigned_samples",
      Cell: ({ value }) => value || "0",
    },
    {
      Header: t ? t("superAdmin.table.columns.createdBy", "Created By") : "Created By",
      accessor: "created_by",
      Cell: ({ value }) => value || "-",
    },
  ];

  // Process rows data
  const rows = users.map((user) => ({
    ...user,
    actions: "", // This is needed for the actions column
  }));

  return {
    columns,
    rows,
  };
}

UsersDataTable.propTypes = {
  users: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  t: PropTypes.func,
};

UsersDataTable.defaultProps = {
  t: null,
};
